import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  Fade,
  Skeleton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import {
  useBulkAddItemsToCampaign,
  useCampaignImportCandidates,
} from "../../../../client/hooks/products/promotions.mutations";
import CampaignImportData from "./CampaignImportData";

export default function CampaignCardList({
  campaigns,
  isLoading,
  onViewDetails,
  onEdit,
  onToggleStatus,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const { mutate: exportCandidates } = useCampaignImportCandidates();
  const { mutate: importCandidates } = useBulkAddItemsToCampaign();

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);

  function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function handleExportData(campaign) {
    setIsLoadingBtn(true);
    exportCandidates(
      { campaign },
      {
        onSuccess: (blob) => {
          const fileName = `${campaign.name !== undefined ? campaign.name : "Campanha sem nome"}.xls`;
          downloadBlob(blob, fileName);
        },
        onSettled: () => {
          setIsLoadingBtn(false);
        },
      }
    );
  }
  function handleImporData(campaign) {
    setSelectedCampaign(campaign);
    setImportDialogOpen(true);
  }

  const handleFileSelected = async (file, campaign) => {
    setIsLoadingBtn(true);
    try {
      const data = await file.arrayBuffer(); // lê como binário
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Monta estrutura final
      const payload = {
        promotion_type: campaign.type,
        items: json.map((row) => ({
          item_id: String(row["Item ID"]).trim(),
          deal_price: Number(
            row["Suggested Discounted Price"] ||
              row["deal_price"] ||
              row["preco"] ||
              row["preço"] ||
              row["price"] ||
              row["Price"] ||
              0
          ),
          offer_id: String(row["Offer Id"]).trim() || null,
        })),
      };
      importCandidates({ promotionId: campaign.id, data: payload });
      // Aqui você pode fazer o POST com `api.post("/rota", payload)`
    } catch (err) {
      console.error("Erro ao processar o arquivo:", err);
    } finally {
      setIsLoadingBtn(false);
    }
  };

  const handleMenuOpen = (event, campaign) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return { color: "#00a650", bg: "#e8f5e8" };
      case "pending":
        return { color: "#3483fa", bg: "#e3f2fd" };
      case "finished":
        return { color: "#d32f2f", bg: "#ffebee" };
      default:
        return { color: "#666", bg: "#f5f5f5" };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };
  const calculateProgress = (campaign) => {
    if (!campaign.start_date || !campaign.finish_date) return 0;

    const startDate = new Date(campaign.start_date);
    const endDate = new Date(campaign.finish_date);
    const currentDate = new Date();

    if (currentDate < startDate) return 0;
    if (currentDate > endDate) return 100;

    const total = endDate - startDate;
    const elapsed = currentDate - startDate;
    return Math.round((elapsed / total) * 100);
  };
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return <PlayIcon sx={{ fontSize: 16 }} />;
      case "pending":
        return <PauseIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const renderBenefits = (benefits) => {
    if (!benefits) return null;
    
    const getBenefitTypeLabel = (type) => {
      switch (type) {
        case 'REBATE':
          return 'Cashback';
        case 'DISCOUNT':
          return 'Desconto';
        default:
          return type;
      }
    };

    const getPaymentMethodLabel = (method) => {
      switch (method) {
        case 'ALL':
          return 'Todos os métodos';
        case 'CREDIT_CARD':
          return 'Cartão de crédito';
        case 'DEBIT_CARD':
          return 'Cartão de débito';
        case 'PIX':
          return 'PIX';
        default:
          return method;
      }
    };

    return (
      <Box sx={{ 
        bgcolor: 'rgba(52, 131, 250, 0.04)', 
        p: 2, 
        borderRadius: 2, 
        mt: 2,
        border: '1px solid rgba(52, 131, 250, 0.1)' 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <LocalOfferIcon sx={{ color: '#3483fa', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3483fa' }}>
            Benefícios da Campanha
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
              Tipo:
            </Typography>
            <Chip 
              label={getBenefitTypeLabel(benefits.type)} 
              size="small"
              sx={{ 
                bgcolor: '#e8f5e8', 
                color: '#00a650',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          </Box>
          
          {benefits.meli_percent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                ML:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6600' }}>
                {benefits.meli_percent}%
              </Typography>
            </Box>  
          )}
          
          {benefits.seller_percent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WalletIcon sx={{ color: '#00a650', fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                Vendedor:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#00a650' }}>
                {benefits.seller_percent}%
              </Typography>
            </Box>
          )}
          
          {benefits.payment_method && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                Pagamento:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getPaymentMethodLabel(benefits.payment_method)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {[...Array(6)].map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={160}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Box>
    );
  }

  const filteredCampaigns = campaigns.results || [];

  if (filteredCampaigns.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <TrendingUpIcon sx={{ fontSize: 60, color: "grey.400" }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhuma campanha encontrada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crie sua primeira campanha para começar a promover seus produtos
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {filteredCampaigns.map((campaign) => {
        const statusConfig = getStatusColor(campaign.status);
        const progress = calculateProgress(campaign);

        return (
          <Fade in key={campaign.id}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.200",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 32px rgba(52, 131, 250, 0.12)",
                  borderColor: "#3483fa",
                },
              }}
              onClick={() => onViewDetails(campaign)}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        fontWeight: 700,
                        fontSize: "1.2rem",
                      }}
                    >
                      {(campaign.name || "C").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "grey.800",
                          fontSize: "1.1rem",
                          mb: 0.5,
                        }}
                      >
                        {campaign.name || "Campanha sem nome"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                      >
                        ID: {campaign.id || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      icon={getStatusIcon(campaign.status)}
                      label={campaign.status || "Desconhecido"}
                      sx={{
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 28,
                        borderRadius: 2,
                      }}
                    />{" "}
                    <Button
                      variant="outlined"
                      disabled={isLoadingBtn}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // <--- impede que o clique suba para o Card
                        handleExportData(campaign);
                      }}
                      startIcon={
                        isLoadingBtn ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FileDownloadIcon />
                        )
                      }
                      sx={{
                        borderColor: "#3483fa",
                        color: "#3483fa",
                        bgcolor: "rgba(52, 131, 250, 0.04)",
                        "&:hover": {
                          bgcolor: "rgba(52, 131, 250, 0.08)",
                          borderColor: "#2968c8",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(52, 131, 250, 0.2)",
                        },
                        "&:disabled": {
                          borderColor: "#ccc",
                          color: "#999",
                          bgcolor: "rgba(0,0,0,0.02)",
                        },
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        minWidth: 120,
                        transition: "all 0.2s ease",
                      }}
                    >
                      Exportar
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // <--- impede que o clique suba para o Card
                        handleImporData(campaign);
                      }}
                      startIcon={<FileUploadIcon />}
                      sx={{
                        bgcolor: "#00a650",
                        color: "white",
                        "&:hover": {
                          bgcolor: "#008a43",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(0, 166, 80, 0.3)",
                        },
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        minWidth: 120,
                        transition: "all 0.2s ease",
                      }}
                    >
                      Aderir
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, campaign)}
                      sx={{
                        bgcolor: "grey.100",
                        "&:hover": { bgcolor: "grey.200" },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 4,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PeopleIcon sx={{ color: "#3483fa", fontSize: 20 }} />{" "}
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {campaign.type || "N/A"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarIcon sx={{ color: "#00a650", fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(campaign.start_date)} -{" "}
                      {formatDate(campaign.finish_date)}
                    </Typography>
                  </Box>
                </Box>

                {campaign.status?.toLowerCase() === "started" && (
                  <Box sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Progresso da campanha
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          bgcolor:
                            progress < 30
                              ? "#00a650"
                              : progress < 70
                                ? "#ff6600"
                                : "#d32f2f",
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}

                {renderBenefits(campaign.benefits)}
              </CardContent>
            </Card>
          </Fade>
        );
      })}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onViewDetails(selectedCampaign);
            handleMenuClose();
          }}
          sx={{ gap: 1 }}
        >
          <VisibilityIcon fontSize="small" />
          Ver detalhes
        </MenuItem>
        <MenuItem
          onClick={() => {
            onEdit(selectedCampaign);
            handleMenuClose();
          }}
          sx={{ gap: 1 }}
        >
          <EditIcon fontSize="small" />
          Editar
        </MenuItem>
        {selectedCampaign?.status?.toLowerCase() === "started" ? (
          <MenuItem
            onClick={() => {
              onToggleStatus(selectedCampaign, "pause");
              handleMenuClose();
            }}
            sx={{ gap: 1 }}
          >
            <PauseIcon fontSize="small" />
            Pausar
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              onToggleStatus(selectedCampaign, "activate");
              handleMenuClose();
            }}
            sx={{ gap: 1 }}
          >
            <PlayIcon fontSize="small" />
            Ativar
          </MenuItem>
        )}
      </Menu>{" "}
      <CampaignImportData
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onFileSelected={handleFileSelected}
        campaign={selectedCampaign}
      />
    </Box>
  );
}
